import React from 'react';

/* Duotone icon library — one distinct, purpose-drawn glyph per real service
   (keyed by the service's own slug from sectorServicesData.js), instead of a
   handful of generic shapes reused across unrelated services. Each icon pairs
   a soft low-opacity fill with a solid line-art layer on top so it reads as
   more than a thin wireframe outline at small sizes. */
const ICONS = {
  /* ── Healthcare ── */
  'neonatal-care-solutions': (c) => (
    <>
      <path d="M4 20V11a8 8 0 0116 0v9" stroke={c} strokeWidth="1.6" fill={`${c}1F`} strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 20h20" stroke={c} strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M7 14.5l1.6 2.5 1.7-4.5 1.6 3.5 1.7-2.5" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'phototherapy-treatment-systems': (c) => (
    <>
      <circle cx="12" cy="5.5" r="2.6" fill={`${c}30`} stroke={c} strokeWidth="1.5"/>
      <path d="M8 10l-2 4M16 10l2 4M12 10v5" stroke={c} strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M3.5 19.5a8.5 3 0 0017 0" stroke={c} strokeWidth="1.6" fill={`${c}1F`} strokeLinecap="round"/>
    </>
  ),
  'remote-patient-monitoring': (c) => (
    <>
      <path d="M4.5 9a7.5 7.5 0 0115 0" stroke={c} strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.6"/>
      <rect x="7.5" y="8" width="9" height="11" rx="3" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <path d="M9.3 13.3h1.6l1-2 1.2 4 1-2h1.6" stroke={c} strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'nutrition-assessment-digital-health': (c) => (
    <>
      <path d="M12 8.3c-3 0-5 2.3-5 5.6 0 3.2 2.2 6.1 5 6.1s5-2.9 5-6.1c0-3.3-2-5.6-5-5.6z" fill={`${c}22`} stroke={c} strokeWidth="1.6"/>
      <path d="M12.2 8.3c0-1.9.9-3 2.3-3.6" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M12 5.4c.9-1.1 2.3-1 2.9-.3" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M9.3 14v3.2M12 12.6v4.6M14.7 14.8v1.8" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
    </>
  ),
  'connected-medical-devices': (c) => (
    <>
      <path d="M12 12L5 6M12 12l7-6M12 12v8" stroke={c} strokeWidth="1.3" strokeLinecap="round" opacity="0.65"/>
      <circle cx="12" cy="12" r="2.3" fill={c}/>
      <circle cx="5" cy="6" r="2.3" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <circle cx="19" cy="6" r="2.3" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <circle cx="12" cy="20.3" r="2.3" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <path d="M4.15 5.15h1.7M5 4.3v1.7" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
    </>
  ),

  /* ── Utilities & Energy ── */
  'energy-monitoring-analytics': (c) => (
    <>
      <path d="M4 20v-6.5M9.3 20V9M14.7 20v-9M20 20V5.5" stroke={c} strokeWidth="1.9" strokeLinecap="round"/>
      <path d="M4 20v-6.5M9.3 20V9M14.7 20v-9M20 20V5.5" stroke={c} strokeWidth="5" strokeLinecap="round" opacity="0.14"/>
      <path d="M19.6 2.6l-2 3.6h2.1l-2 3.5" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'battery-energy-storage-monitoring': (c) => (
    <>
      <rect x="2.5" y="8" width="16" height="8" rx="2" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <rect x="19" y="10.3" width="2" height="3.4" rx="0.7" fill={c}/>
      <rect x="4.5" y="10" width="4" height="4" rx="0.7" fill={c} opacity="0.55"/>
      <path d="M9.5 12h1.6l1-2.2 1.2 4.4 1-2.2h1.7" stroke={c} strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'dg-automation-remote-control': (c) => (
    <>
      <rect x="2.5" y="10" width="10.5" height="8.5" rx="1.6" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <circle cx="6" cy="14.3" r="1.3" fill={c}/>
      <circle cx="9.5" cy="14.3" r="1.3" fill={c}/>
      <path d="M16.3 8a5 5 0 010 8" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.8"/>
      <path d="M18.7 5.6a8.4 8.4 0 010 12.8" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
    </>
  ),
  'renewable-energy-solar-monitoring': (c) => (
    <>
      <path d="M2.5 9.5l2-5.5h9.4l2 5.5-2 8.5H4.5z" stroke={c} strokeWidth="1.5" fill={`${c}1F`} strokeLinejoin="round"/>
      <path d="M5.4 9.5h9.6M7 4l-1.4 13.6M9.7 4l-.5 13.6M12.4 4l.9 13.6" stroke={c} strokeWidth="1" opacity="0.55"/>
      <circle cx="19" cy="4.6" r="2.3" fill={`${c}30`} stroke={c} strokeWidth="1.4"/>
      <path d="M19 .9v1M22.7 4.6h-1M19 8.3v1M16.3 4.6h1" stroke={c} strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
    </>
  ),
  'industrial-electrical-asset-monitoring': (c) => (
    <>
      <circle cx="8.5" cy="8.5" r="4" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <circle cx="8.5" cy="15.5" r="4" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <path d="M8.5 3v18" stroke={c} strokeWidth="1.1" opacity="0.45"/>
      <path d="M15.5 8.5l3.3 3.5-3.3 3.5" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'predictive-maintenance-industrial-iot': (c) => (
    <>
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l2.5-2.5a4 4 0 01-5.3 5.3l-6.2 6.2a1.6 1.6 0 01-2.2-2.2l6.2-6.2a4 4 0 015.3-5.3z" stroke={c} strokeWidth="1.4" fill={`${c}1F`} strokeLinejoin="round"/>
      <path d="M2.5 16.3h1.7l1-2.4 1.3 4.7 1-2.3h1.6" stroke={c} strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
    </>
  ),

  /* ── Industrial IoT & Automation ── */
  'smart-factory-machine-monitoring': (c) => (
    <>
      <path d="M2.5 20V11l4.2 3v-3l4.2 3v-3l4.2 3V8h6v12z" stroke={c} strokeWidth="1.5" fill={`${c}1F`} strokeLinejoin="round"/>
      <rect x="15.3" y="14.5" width="3.6" height="3.2" rx="0.5" stroke={c} strokeWidth="1.2" fill="none"/>
      <circle cx="19.5" cy="5" r="1.4" fill={c}/>
      <path d="M19.5 6.4V8" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>
    </>
  ),
  'predictive-maintenance-solutions': (c) => (
    <>
      <circle cx="8.7" cy="12" r="5" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <path d="M8.7 3.2v2.3M8.7 18.5v2.3M0 12h2.3M15.1 12h2.3M2.9 6.2l1.6 1.6M11.9 15.2l1.6 1.6M2.9 17.8l1.6-1.6M11.9 8.8l1.6-1.6" stroke={c} strokeWidth="1.2" strokeLinecap="round" opacity="0.55"/>
      <path d="M14.2 12h1.6l1-2.4 1.3 4.7 1-2.3h1.7" stroke={c} strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </>
  ),
  'industrial-data-acquisition-dashboards': (c) => (
    <>
      <rect x="2.5" y="3.5" width="8" height="6.5" rx="1.4" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
      <rect x="13.5" y="3.5" width="8" height="6.5" rx="1.4" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
      <rect x="2.5" y="14" width="8" height="6.5" rx="1.4" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
      <rect x="13.5" y="14" width="8" height="6.5" rx="1.4" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
      <path d="M4.3 8.4l1.6-1.8 1.3 1 1.8-2.4" stroke={c} strokeWidth="1.1" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"/>
    </>
  ),
  'connected-industrial-iot-platforms': (c) => (
    <>
      <path d="M12 10L4.3 4.9M12 10l7.7-5.1M12 14l-7.7 5.1M12 14l7.7 5.1" stroke={c} strokeWidth="1.2" opacity="0.55"/>
      <rect x="9.7" y="9.7" width="4.6" height="4.6" rx="1" fill={c}/>
      <rect x="2" y="1.9" width="4.4" height="4.4" rx="1" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <rect x="17.6" y="1.9" width="4.4" height="4.4" rx="1" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <rect x="2" y="17.7" width="4.4" height="4.4" rx="1" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
      <rect x="17.6" y="17.7" width="4.4" height="4.4" rx="1" stroke={c} strokeWidth="1.4" fill={`${c}1F`}/>
    </>
  ),
  'industrial-asset-utility-monitoring': (c) => (
    <>
      <path d="M2.5 7.5h6a3 3 0 013 3v3a3 3 0 003 3h5.5" stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
      <circle cx="11.5" cy="12" r="2" fill={`${c}30`} stroke={c} strokeWidth="1.3"/>
      <circle cx="18.7" cy="6" r="2.6" stroke={c} strokeWidth="1.3" fill={`${c}1F`}/>
      <path d="M18.7 6l1.2-1.6" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
    </>
  ),
  'industrial-automation-control-systems': (c) => (
    <>
      <circle cx="4" cy="19.3" r="1.9" fill={c}/>
      <path d="M4 17.4V12.8l5-3 4.2 3.2v2.8" stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12.4 11.4l2.6-1.4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="15.3" y="6.7" width="5" height="5" rx="1.2" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
    </>
  ),

  /* ── Education ── */
  'stem-iot-learning-kits': (c) => (
    <>
      <rect x="2.5" y="4.5" width="14" height="10.5" rx="1.6" stroke={c} strokeWidth="1.5" fill={`${c}1F`}/>
      <circle cx="6.3" cy="8.4" r="1.1" fill={c}/>
      <circle cx="10.2" cy="11.4" r="1.1" fill={c}/>
      <circle cx="13.3" cy="7.7" r="1.1" fill={c}/>
      <path d="M6.3 8.4h3.9M10.2 11.4h3.1M8.4 8.4v3" stroke={c} strokeWidth="0.9" opacity="0.55"/>
      <path d="M18.7 5.4a2.9 2.9 0 00-2.9 2.9c0 1.2.7 1.9 1.35 2.5.4.4.55.85.55 1.3h2c0-.45.15-.9.55-1.3.65-.6 1.35-1.3 1.35-2.5a2.9 2.9 0 00-2.9-2.9z" stroke={c} strokeWidth="1.3" fill={`${c}30`}/>
    </>
  ),
  'robotics-automation-labs': (c) => (
    <>
      <path d="M12 6.6V3.6" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="12" cy="3" r="1.3" fill={c}/>
      <rect x="5.5" y="6.6" width="13" height="10.4" rx="3.2" stroke={c} strokeWidth="1.6" fill={`${c}1F`}/>
      <circle cx="9.5" cy="11.6" r="1.4" fill={c}/>
      <circle cx="14.5" cy="11.6" r="1.4" fill={c}/>
      <path d="M9.5 15.2h5" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
    </>
  ),
  'engineering-lab-development': (c) => (
    <>
      <path d="M10.2 2.8h4M10.6 2.8v4.5l-4 7.7a2.1 2.1 0 001.9 3.1h7a2.1 2.1 0 001.9-3.1l-4-7.7V2.8" stroke={c} strokeWidth="1.5" fill={`${c}1F`} strokeLinejoin="round"/>
      <path d="M8.2 14h7.9" stroke={c} strokeWidth="1.1" opacity="0.55"/>
      <circle cx="18.6" cy="6.3" r="2.3" stroke={c} strokeWidth="1.3" fill="none"/>
      <path d="M18.6 3.6v.9M18.6 8.4v.9M16.3 6.3h.9M20.1 6.3h.9M16.9 4.6l.65.65M19.65 7.35l.65.65M16.9 8l.65-.65M19.65 5.25l.65-.65" stroke={c} strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
    </>
  ),
  'cloud-based-learning-platforms': (c) => (
    <>
      <path d="M6.7 18h10.5a4 4 0 000-8 5.6 5.6 0 00-10.8-1.7A4 4 0 006.7 18z" stroke={c} strokeWidth="1.5" fill={`${c}1F`} strokeLinejoin="round"/>
      <path d="M12 9.2L6.8 11.6l5.2 2.4 5.2-2.4z" fill={c} opacity="0.85"/>
      <path d="M8.9 12.8v1.9c0 .9 1.35 1.6 3.1 1.6s3.1-.7 3.1-1.6v-1.9" stroke={c} strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    </>
  ),
};

export const SERVICE_ICON_SLUGS = new Set(Object.keys(ICONS));

export function ServiceIcon({ slug, color, size = 18 }) {
  const render = ICONS[slug];
  if (!render) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {render(color)}
    </svg>
  );
}
